import { useRef, useEffect, useContext } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../Context/AppContext";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import CommunityCard from "./components/CommunityCard";
import IncomeGraph from "./components/IncomeGraph";
import { communitiesOverviewMock, months12 } from "./mockData";

const fmt = (n) => Number(n).toLocaleString("en-US");
// Wide cover banners (landscape) for the community cards — instant recognition.
const COVERS = [
  "/mock-projects/center.jpg",
  "/mock-projects/garden.jpg",
  "/mock-projects/fund.jpg",
  "/mock-projects/holiday.jpg",
  "/mock-projects/garden-b.jpg",
  "/mock-projects/center-a.jpg",
  "/mock-projects/fund-a.jpg",
];
const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
const TEXT_MAIN = "rgba(47, 43, 61, 0.9)";
const TEXT_SUB = "rgba(71, 84, 103, 1)";
const AMBER = "rgba(180, 120, 20, 1)";

const o = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: { xs: "75px", md: "0px" },
    paddingBottom: "24px",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  aggGrid: {
    display: "grid",
    // 4 stacked rows on mobile (numbers get the full width), 4-up on desktop.
    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(4, minmax(0, 1fr))" },
    gap: "12px",
  },
  aggTile: { border: CARD_BORDER, borderRadius: "12px", backgroundColor: "#fff", padding: "16px", display: "flex", alignItems: "center", gap: "12px", minWidth: 0 },
  aggIcon: (bg) => ({ width: "44px", height: "44px", minWidth: "44px", borderRadius: "50%", backgroundColor: bg, display: "flex", alignItems: "center", justifyContent: "center" }),
  aggValue: { fontSize: "22px", fontWeight: 600, lineHeight: "120%", color: TEXT_MAIN },
  aggLabel: { fontSize: "13px", color: TEXT_SUB },
  aggSub: { fontSize: "12px", color: TEXT_SUB },

  graphCard: { border: CARD_BORDER, borderRadius: "12px", backgroundColor: "#fff", padding: "18px", maxWidth: "100%", overflow: "hidden" },

  // Horizontally scrollable carousel: native touch scroll + a visible thin
  // scrollbar, plus mouse click-and-drag (wired below) so desktop can slide too.
  track: {
    display: "flex",
    gap: "16px",
    overflowX: "auto",
    marginTop: "8px",
    paddingBottom: "10px",
    cursor: "grab",
    userSelect: "none",
    scrollSnapType: "x proximity",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(47, 43, 61, 0.25) transparent",
    "&:active": { cursor: "grabbing" },
    "& img": { WebkitUserDrag: "none" },
    "&::-webkit-scrollbar": { height: "8px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(47, 43, 61, 0.22)", borderRadius: "4px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "rgba(47, 43, 61, 0.05)", borderRadius: "4px" },
  },
  slide: {
    // ~2.5 cards visible on desktop so the peeking card signals "scrollable".
    flex: { xs: "0 0 82%", sm: "0 0 56%", md: "0 0 38%" },
    minWidth: 0,
    scrollSnapAlign: "start",
  },
};

const CommunitiesOverviewContainer = () => {
  const { t, i18n } = useTranslation("page-communities");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";
  const isRTL = lang === "he";
  const loc = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v[lang] ?? v.en : v);

  // Multi-community feature only: a CM with one community has no portfolio to
  // manage, so send them to their community dashboard instead of showing a
  // "list of one". (The sidebar link is already hidden for them too.)
  const { userCommunities, isLoading } = useContext(AppContext);
  useEffect(() => {
    if (!isLoading && (userCommunities?.length ?? 0) <= 1) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoading, userCommunities, navigate]);

  const m = communitiesOverviewMock;
  const cur = m.currencySymbol;
  const money = (n) => `${cur}${fmt(n)}`;
  const list = m.communities;

  // Mouse drag-to-scroll (desktop). Touch uses native scrolling.
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });
  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return;
    drag.current = { down: true, startX: e.clientX, startScroll: trackRef.current.scrollLeft };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    trackRef.current.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  // Smoothly bring a card to the reading-start edge (used by the "needs" tile).
  const scrollToIndex = (index) => {
    const track = trackRef.current;
    const card = track?.children?.[index];
    if (!track || !card) return;
    const tRect = track.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const start = track.scrollLeft;
    const target = start + (isRTL ? cRect.right - tRect.right : cRect.left - tRect.left);
    const t0 = performance.now();
    const ease = (x) => 1 - Math.pow(1 - x, 3);
    const prevSnap = track.style.scrollSnapType;
    track.style.scrollSnapType = "none";
    const tick = () => {
      const p = Math.min(1, (performance.now() - t0) / 350);
      track.scrollLeft = start + (target - start) * ease(p);
      if (p < 1) setTimeout(tick, 16);
      else track.style.scrollSnapType = prevSnap;
    };
    tick();
  };
  const jumpToFirstNeedy = () => {
    const i = list.findIndex((c) => (c.needs.joinRequests || 0) + (c.needs.supplierOffers || 0) > 0);
    if (i >= 0) scrollToIndex(i);
  };

  const agg = list.reduce(
    (acc, c) => {
      acc.members += c.members.current;
      acc.suppliers += c.suppliers;
      acc.revenue += c.revenueThisMonth;
      acc.earnings += c.earningsThisMonth;
      acc.needs += (c.needs.joinRequests || 0) + (c.needs.supplierOffers || 0);
      if (c.status === "active") acc.active += 1;
      return acc;
    },
    { members: 0, suppliers: 0, revenue: 0, earnings: 0, needs: 0, active: 0 },
  );

  const onNeedClick = (community, target) =>
    navigate(target === "suppliers" ? "/my-suppliers" : "/my-members");

  // On phones the 12-month graph is too dense — show only the last 3 months.
  const monthsToShow = isMobile ? 3 : 12;
  const graphSeries = list.map((c) => ({ name: loc(c.name), color: c.color, values: c.income12mo.slice(-monthsToShow) }));
  const graphLabels = months12.slice(-monthsToShow).map((mo) => loc(mo));

  const aggTiles = [
    { key: "communities", icon: <GroupsOutlinedIcon sx={{ color: "#7367F0", fontSize: 22 }} />, bg: "rgba(242, 243, 251, 1)", value: fmt(list.length), label: t("agg.communities"), sub: t("agg.active", { count: agg.active }) },
    { key: "members", icon: <PeopleAltOutlinedIcon sx={{ color: "#ff9f43", fontSize: 22 }} />, bg: "rgba(255, 244, 229, 1)", value: fmt(agg.members), label: t("agg.members") },
    { key: "revenue", icon: <PaymentsOutlinedIcon sx={{ color: "rgba(36, 139, 112, 1)", fontSize: 22 }} />, bg: "rgba(219, 240, 236, 1)", value: money(agg.revenue), label: t("agg.revenue"), sub: t("agg.earnings", { money: money(agg.earnings) }) },
    { key: "needs", icon: <NotificationsActiveOutlinedIcon sx={{ color: AMBER, fontSize: 22 }} />, bg: "rgba(255, 244, 229, 1)", value: fmt(agg.needs), label: t("agg.needs"), valueColor: agg.needs > 0 ? AMBER : TEXT_MAIN, onClick: agg.needs > 0 ? jumpToFirstNeedy : undefined },
  ];

  return (
    <Box sx={o.page}>
      <Box sx={o.aggGrid}>
        {aggTiles.map((tile) => (
          <Box
            key={tile.key}
            onClick={tile.onClick}
            sx={{ ...o.aggTile, ...(tile.onClick ? { cursor: "pointer", "&:hover": { backgroundColor: "rgba(245, 246, 250, 1)" } } : {}) }}
          >
            <Box sx={o.aggIcon(tile.bg)}>{tile.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...o.aggValue, ...(tile.valueColor ? { color: tile.valueColor } : {}) }}>{tile.value}</Typography>
              <Typography sx={o.aggLabel}>{tile.label}</Typography>
              {tile.sub ? <Typography sx={o.aggSub}>{tile.sub}</Typography> : null}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={o.graphCard}>
        <IncomeGraph series={graphSeries} labels={graphLabels} currencySymbol={cur} hint={t("graph_hint")} />
      </Box>

      <Box
        ref={trackRef}
        sx={o.track}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {list.map((community, i) => (
          <Box key={community.id} sx={o.slide}>
            <CommunityCard community={community} cover={COVERS[i % COVERS.length]} loc={loc} money={money} t={t} isRTL={isRTL} onNeedClick={onNeedClick} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CommunitiesOverviewContainer;
